import {
    AvatarStackCta,
    BgCoverCta,
    BookshelfCta,
    CallToAction,
    CardCta,
    FlagCta,
    IssueCoverArtCta,
    SplitCta,
    YoutubeCardCta,
} from "@components/call-to-action";
import { AffiliateUrls, AttrProps, Breakpoints, Pagination as IPagination } from "@customTypes/index";
import useResponsiveCmsSettings from "@hooks/useResponsiveCmsSettings";
import { CtaTypes } from "@lib/constants";
import { Button, Col, Empty, Pagination, Row } from "antd";

declare const PostGridCtaOptions: [
    "AvatarStack",
    "Cover",
    "Bookshelf",
    "CallToAction",
    "Card",
    "CardCta",
    "Stacked",
    "Flag",
    "Split",
    "TopTenSplit",
    "Youtube",
    "IssueCoverArt"
];
export declare type PostGridCtaOption = typeof PostGridCtaOptions[number];

declare const PostGridPaginationOptions: [
    "Numbers",
    "LoadMore",
    "none",
];
export declare type PostGridPaginationOption = typeof PostGridPaginationOptions[number];

const components: any = {
    AvatarStack: AvatarStackCta,
    Cover: BgCoverCta,
    Bookshelf: BookshelfCta,
    CallToAction: CallToAction,
    Card: CardCta,
    Stacked: CardCta,
    Flag: FlagCta,
    Split: SplitCta,
    Youtube: YoutubeCardCta,
    IssueCoverArt: IssueCoverArtCta,
};

interface PostGridCtaProps {
    type: PostGridCtaOption;
    flip?: string;
    imgRatio?: string;
    layout?: number[];
}

interface PostGridProps {
    className?: string;
    playlistId?: number;
    perRow?: any;
    data: AttrProps[];
    cta?: PostGridCtaProps;
    showFields?: string[];
    breakpoints?: Breakpoints,
    affiliateUrls?: AffiliateUrls,
    onPaginationChange?: any;
    onPaginationSizeChange?: any;
    pagination?: IPagination;
    isLoading?: boolean;
    flip?: string;
}

const defaultProps: PostGridProps = {
    data: [],
    cta: { type: 'CardCta', imgRatio: 'none' },
    showFields: [],
    pagination: {
        simple: false,
        type: 'none',
        current_page: 0,
        per_page: 0,
        next_page: 0,
        prev_page: 0,
        max_pages: 0,
        total_items: 0,
    }
};

const PostGridItems = ({
    data,
    className,
    perRow,
    cta,
    showFields,
}: any) => {
    return (
        <>
            {data?.map((item, i) => {
                const DyntamicComponent = components[cta.type];
                const colXs = [
                    CtaTypes.COVER,
                    CtaTypes.YOUTUBE,
                    CtaTypes.ISSUE_COVERART
                ].includes(cta.type)
                    ? 12
                    : 24;

                return (
                    <Col
                        key={`${item.id}-${i}`}
                        className={className}
                        lg={(24 / perRow)}
                        xs={perRow === 1 ? 24 : colXs}
                    >
                        <DyntamicComponent
                            showFields={showFields}
                            {...item}
                            {...cta}
                            images={item.gallery}
                            link={{
                                url: item?.link?.url,
                                title: item?.link?.title,
                                target: item?.link?.target,
                            }}
                        />
                    </Col>
                );
            })}
        </>
    );
};

const PostGrid = ({
    className,
    data,
    cta,
    perRow,
    isLoading,
    pagination,
    showFields,
    affiliateUrls,
    breakpoints,
    onPaginationChange,
    onPaginationSizeChange
}: PostGridProps) => {

    const empty = (
        <Col span={24}><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} /></Col>
    );

    const { gutter } = useResponsiveCmsSettings(breakpoints);

    return (
        <>
            <Row gutter={gutter ? [gutter, gutter] : [16, 16]}>
                {data?.length > 0
                    ? <PostGridItems
                        className={className}
                        data={data}
                        cta={cta}
                        perRow={perRow}
                        showFields={showFields}
                        affiliateUrls={affiliateUrls}
                    />
                    : empty}
            </Row>
            {pagination?.type === 'Numbers'
                && <Row justify={`end`}>
                    <Col span={24}>
                        <hr className={`border-b-1`} />
                    </Col>
                    <Col span={24}>
                        <Pagination
                            simple={pagination?.simple}
                            current={pagination?.current_page}
                            pageSize={pagination?.per_page}
                            total={pagination?.total_items}
                            onChange={onPaginationChange
                                ? onPaginationChange
                                : () => { }}
                            onShowSizeChange={onPaginationSizeChange
                                ? onPaginationSizeChange
                                : () => { }}
                        />
                    </Col>
                </Row>}

            {pagination?.type === 'LoadMore'
                && pagination?.current_page
                && pagination?.max_pages
                && pagination?.current_page <= pagination
                    ?.max_pages
                && <Row justify="center">
                    <Col span={24}>
                        <Button
                            onClick={onPaginationChange
                                ? () => onPaginationChange(pagination
                                    ?.current_page, pagination?.per_page)
                                : () => { }}
                            type="primary" size="large"
                            loading={isLoading}
                        >
                            LoadMore
                        </Button>
                    </Col>
                </Row>}
        </>
    );
};

PostGrid.defaultProps = defaultProps;

export default PostGrid;
